import { useRef, useState, useEffect, forwardRef } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Share2, Copy, Check, Loader2, Sparkles, CheckCircle } from "lucide-react";
import { useCountdown } from "@/hooks/useCountdown";
import {
  EVENT_DEPARTURE,
  EVENT_YEAR,
  EVENT_TAGLINE,
  SITE_URL,
  LOGO_URL,
  DUNDER_GUBBE_2026_URL,
} from "@/config/event";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_TITLE = "Dunderkryssningen 2026";

const shareUrl = (code) => (code ? `${SITE_URL}/?ref=${encodeURIComponent(code)}` : SITE_URL);
const shareText = (days, code) =>
  `🚢 DUNDERKRYSSNINGEN ${EVENT_YEAR} – ${EVENT_TAGLINE}! ${
    days > 0 ? `Bara ${days} dagar kvar. ` : ""
  }${code ? `Använd min kod ${code} och få VIP-band 🔥` : "Köp biljett via en ambassadör och få VIP-band 🔥"}`;

export const ShareSection = () => {
  const t = useCountdown(EVENT_DEPARTURE);
  const cardRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeValid, setCodeValid] = useState(null);
  const [ambassadorName, setAmbassadorName] = useState("");

  const code = codeInput.trim().toUpperCase();
  const activeCode = codeValid ? code : "";

  useEffect(() => {
    if (code.length < 2) {
      setCodeValid(null);
      setAmbassadorName("");
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/validate-code/${code}`);
        setCodeValid(data.valid);
        setAmbassadorName(data.valid ? data.ambassador : "");
      } catch {
        setCodeValid(false);
        setAmbassadorName("");
      }
    }, 400);
    return () => clearTimeout(id);
  }, [code]);

  const buildImage = () =>
    toPng(cardRef.current, { cacheBust: true, pixelRatio: 3, backgroundColor: "#050505" });

  const fileName = `dunderkryssningen-2026${activeCode ? `-${activeCode}` : ""}.png`;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    try {
      const dataUrl = await buildImage();
      const link = document.createElement("a");
      link.download = fileName;
      link.href = dataUrl;
      link.click();
      toast.success("Story-kort nedladdat! Dela det i din story 🔥");
    } catch {
      toast.error("Kunde inte skapa bilden. Försök igen.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    const text = shareText(t.days, activeCode);
    const url = shareUrl(activeCode);
    try {
      const dataUrl = await buildImage();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: SHARE_TITLE, text, url, files: [file] });
        return;
      }
    } catch {
      /* fall through to text/url share */
    }
    try {
      if (navigator.share) {
        await navigator.share({ title: SHARE_TITLE, text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Länk kopierad – klistra in och dela!");
      }
    } catch {
      /* user cancelled – ignore */
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText(t.days, activeCode)} ${shareUrl(activeCode)}`);
      setCopied(true);
      toast.success("Länk & text kopierad!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  return (
    <section
      className="w-full max-w-md flex flex-col items-center"
      aria-label="Dela eventet"
      data-testid="share-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-[#FFD700]" aria-hidden="true" />
        <h3 className="font-heading text-lg md:text-xl text-white uppercase tracking-wide">
          Sprid Dunder
        </h3>
      </div>
      <p className="text-gray-500 text-sm text-center mb-5">
        Ladda ner ditt story-kort och dela med ditt gang
      </p>

      {/* Ambassador code input */}
      <div className="w-full max-w-[300px] mb-5">
        <label
          htmlFor="share_code"
          className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FFD700] mb-2 block"
        >
          Din ambassadör-kod
        </label>
        <Input
          id="share_code"
          type="text"
          placeholder="Skriv din kod"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value)}
          maxLength={20}
          className={`bg-[#1A1A1A] border-2 text-white placeholder:text-gray-600 focus:ring-2 focus:ring-[#FFD700] h-11 rounded-sm uppercase tracking-widest ${
            codeValid === true ? "border-green-500" : codeValid === false ? "border-red-500" : "border-transparent"
          }`}
          data-testid="share-code-input"
        />
        {codeValid === true && (
          <p className="mt-2 text-green-400 text-xs flex items-center gap-1" data-testid="share-code-valid">
            <CheckCircle className="w-3.5 h-3.5" /> Ambassadör: {ambassadorName}
          </p>
        )}
        {codeValid === false && (
          <p className="mt-2 text-red-400 text-xs" data-testid="share-code-invalid">
            Ogiltig kod
          </p>
        )}
      </div>

      <StoryCard ref={cardRef} days={t.days} isOver={t.isOver} code={activeCode} />

      {/* Actions */}
      <div className="w-full mt-6 grid grid-cols-1 gap-3">
        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          className="w-full bg-[#FFD700] text-black font-bold uppercase tracking-widest hover:bg-[#E5C100] hover:scale-[1.02] transition-all duration-300 h-12 rounded-sm gold-glow gold-glow-hover"
          data-testid="download-story-button"
        >
          {isDownloading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Skapar bild...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" /> Ladda ner story-kort
            </>
          )}
        </Button>
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={handleShare}
            variant="outline"
            className="border border-[#FFD700]/40 bg-transparent text-[#FFD700] hover:bg-[#FFD700]/10 hover:border-[#FFD700] uppercase tracking-wider h-12 rounded-sm"
            data-testid="share-button"
          >
            <Share2 className="mr-2 h-4 w-4" /> Dela
          </Button>
          <Button
            onClick={handleCopy}
            variant="outline"
            className="border border-[#333] bg-transparent text-gray-300 hover:border-[#FFD700] hover:text-[#FFD700] uppercase tracking-wider h-12 rounded-sm"
            data-testid="copy-link-button"
          >
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4" /> Kopierat
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" /> Kopiera
              </>
            )}
          </Button>
        </div>
      </div>
    </section>
  );
};

const StoryCard = forwardRef(({ days, isOver, code }, ref) => (
  <div
    ref={ref}
    data-testid="story-card"
    className="relative w-[300px] h-[534px] bg-[#050505] overflow-hidden border border-[#FFD700]/30 rounded-sm shadow-[0_0_50px_rgba(255,215,0,0.15)]"
  >
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(circle at 50% 18%, rgba(255,215,0,0.18) 0%, rgba(5,5,5,0) 50%), radial-gradient(circle at 15% 95%, rgba(255,215,0,0.10) 0%, rgba(5,5,5,0) 40%)",
      }}
      aria-hidden="true"
    />
    <div className="absolute inset-3 border border-[#FFD700]/20 rounded-sm pointer-events-none" aria-hidden="true" />

    <div className="relative z-10 h-full flex flex-col items-center px-6 pt-8 pb-8">
      <img
        src={LOGO_URL}
        alt="Dunderkryssningen"
        crossOrigin="anonymous"
        className="w-[165px] drop-shadow-[0_0_18px_rgba(255,215,0,0.25)]"
      />

      <div className="mt-2 flex items-center gap-3 w-full">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#FFD700]/50" aria-hidden="true" />
        <span className="text-[13px] font-bold tracking-[0.45em] text-white/90" data-testid="story-card-year">
          {EVENT_YEAR}
        </span>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#FFD700]/50" aria-hidden="true" />
      </div>
      <span className="mt-2 text-[8px] font-bold uppercase tracking-[0.18em] text-gray-300 text-center leading-relaxed" data-testid="story-card-tagline">
        {EVENT_TAGLINE.split(",").map((line, i, arr) => (
          <span key={i} className="block">
            {line.trim()}
            {i < arr.length - 1 ? "," : ""}
          </span>
        ))}
      </span>

      <img
        src={DUNDER_GUBBE_2026_URL}
        alt=""
        crossOrigin="anonymous"
        aria-hidden="true"
        className="w-[110px] mt-1 drop-shadow-[0_10px_30px_rgba(0,0,0,0.6)]"
        data-testid="story-card-mascot"
      />

      <div className="mt-auto w-full flex flex-col items-center">
        <div className="w-full bg-[#0A0A0A]/85 border border-[#FFD700]/30 rounded-sm px-4 py-3 flex items-center gap-4">
          <div className="flex-1 flex flex-col items-start">
            <span className="font-heading text-[40px] leading-none text-white tabular-nums">{days}</span>
            <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#FFD700] mt-1">
              {isOver ? "Vi ses ombord" : "Dagar kvar"}
            </span>
            <div className="mt-3 flex flex-col items-start" data-testid="story-card-code">
              <span className="text-[8px] uppercase tracking-[0.25em] text-gray-500">Min kod</span>
              <span className="font-heading text-[22px] leading-none text-[#FFD700] tracking-wider mt-0.5">
                {code || "DIN KOD"}
              </span>
            </div>
          </div>
          <div className="bg-white p-1.5 rounded-sm" data-testid="story-card-qr">
            <QRCodeSVG value={shareUrl(code)} size={82} level="M" bgColor="#ffffff" fgColor="#050505" />
          </div>
        </div>
        <p className="mt-2 text-[9px] uppercase tracking-[0.22em] text-gray-500 text-center">
          Skanna QR-koden för <span className="text-[#FFD700]">VIP-band</span>
        </p>
        <p className="mt-1 w-full text-center text-[10px] font-bold uppercase tracking-[0.12em] text-white whitespace-nowrap">
          dunderkryssningen.se
        </p>
      </div>
    </div>
  </div>
));
StoryCard.displayName = "StoryCard";
